// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core';

// Internal Dependencies
import { LoyaltyHeaderMenu, Status as ConstantStatus } from '../../../../common/constants';
import Design from './Design/Design';
import StampsMechanics from './StampsMechanics/StampsMechanics';
import Receipt from './Receipt';
import Status from './Status/Status';
import ValidityTerms from './ValidityTerms/ValidityTerms';
import Voucher from './Vouchers/Voucher';
import MButton from '../../../../components/MButton';
import Colours from '../../../../styles/colours';
import Loading from '../../../../components/Loading';
import Outlet from './Outlet/Outlet';
import withLoyaltyConnect from './WithLoyaltyConnect';
import MText from '../../../../components/Common/MText';
import AlertModal from '../../../../components/Common/AlertModal';
import CheckBox from '../../../../components/Common/form/CheckBox';
import ApiService from '../../../../services/apiService';
import { AvailableImages, DesignTypeOptions } from './constants';
import { IsEmpty } from '../../../../common/checks';

const useStyles = makeStyles({
  statusBtn: {
    width:        '100%',
    maxWidth:     120,
    height:       42,
    marginTop:    24,
    borderRadius: 0,
  },
  statusBtnContainer: {
    maxWidth:      170,
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
  },
});

const ModalContent = {
  Status: {
    Live: {
      title:       'Live Loyalty Card?',
      description: 'Loyalty card will be live, and customers can access and start collecting stamps.',
      actionName:  'Live Loyalty Card',
    },
    Update: {
      title:       'Confirm?',
      description: 'Save changes in loyalty card?',
      actionName:  'Save',
    },
    Deactivate: {
      title:       'Deactivate Loyalty Card?',
      description: 'Once deactivated, customers cannot access the loyalty card. Reactivation of the loyalty card will not be possible.',
      actionName:  'Deactivate Loyalty Card',
    },
  },
  VoucherIssuance: {
    Pause: {
      title:       'Pause Voucher Issuance?',
      description: 'Voucher issuance will be paused, and customers will not be able to collect voucher.',
      actionName:  'Pause issuance',
    },
    Continue: {
      title:       'Continue Voucher Issuance?',
      description: 'Voucher issuance will resume, and customers will be able to collect voucher.',
      actionName:  'Continue issuance',
    },
  },
};

const Main = (props) => {
  const classes = useStyles();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const loyaltyStatus = props.loyaltyProgram.status;

  const getSelectedTabContent = () => {
    switch (props.tab) {
      case LoyaltyHeaderMenu.Design:
        return <Design match={props.match} />;
      case LoyaltyHeaderMenu.StampAndMechanics:
        return <StampsMechanics match={props.match} />;
      case LoyaltyHeaderMenu.Receipt:
        return <Receipt />;
      case LoyaltyHeaderMenu.Status:
        return <Status />;
      case LoyaltyHeaderMenu.ValidityAndTerms:
        return <ValidityTerms />;
      case LoyaltyHeaderMenu.Voucher:
        return <Voucher match={props.match} />;
      case LoyaltyHeaderMenu.Outlets:
        return <Outlet match={props.match} />;
      default:
        return null;
    }
  };

  /**
   *
   * @param {string} selectedStatus indicates the status for the loyalty card will be updated to. If empty, we will use the status value from redux state
   *
   */
  const onUpdateLoyaltyCard = async (selectedStatus) => {
    /**
     * The reason why we don't put a try catch block here is to allow the caller function to catch the error.
     */
    const { name, expiryDate, validityInMonths, additionalTnC, status, stamp, design } = props.loyaltyProgram;

    const isUsingTemplate = design.designType === DesignTypeOptions.Template;

    const updatedData = {
      name,
      terms:            additionalTnC,
      validityInMonths: !IsEmpty(validityInMonths) ? validityInMonths : null,
      expiryDate:       !IsEmpty(expiryDate) ? expiryDate : null,
      status:           !IsEmpty(selectedStatus) ? selectedStatus : status,
      bkgrdColour:      !isUsingTemplate ? design.stampsHolderBackgroundColour : null,
      design:           isUsingTemplate ? design.templatePreview : null,
      amountPerStamp:   stamp.amountPerStamp,
      numOfStamps:      stamp.numOfStamps,
    };

    await ApiService.updateLoyaltyCard(props.page.id, props.match.params.loyaltyCardId, updatedData);

    // Cleans cached values when updating loyalty card
    if (isUsingTemplate) {
      props.updateLoyaltyCardAction({
        image:  null,
        design: {
          ...props.loyaltyProgram.design,
          uploadedDesign:               null,
          stampsHolderBackgroundColour: null,
        },
      });
    } else {
      props.updateLoyaltyCardAction({
        design: {
          ...props.loyaltyProgram.design,
          selectedLoyaltyCardTemplate:  AvailableImages[0].top,
          selectedStampsHolderTemplate: AvailableImages[0].btm,
          templatePreview:              AvailableImages[0].image,
        },
      });
    }
  };

  const onSaveAllClick = async () => {
    try {
      props.dispatchStartSavingLC();
      await onUpdateLoyaltyCard();
      toast.success('Loyalty card data saved');
    } catch (error) {
      toast.error(error?.message || 'Failed to update the loyalty card');
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const onConfirm = async () => {
    try {
      setShowConfirmationModal(false);
      setModalContent({});

      props.dispatchStartSavingLC();

      switch (modalContent.actionName) {
        case ModalContent.Status.Live.actionName:
          await onUpdateLoyaltyCard(ConstantStatus.Live);
          props.updateLoyaltyCardAction({
            status:   ConstantStatus.Live,
            vouchers: props.loyaltyProgram.vouchers.map((voucher) => (voucher.status === ConstantStatus.Draft ? {
              ...voucher,
              status: ConstantStatus.Activated,
            } : voucher)),
          });
          toast.success('Loyalty card updated to Live');
          break;
        case ModalContent.Status.Deactivate.actionName:
          await onUpdateLoyaltyCard(ConstantStatus.Deactivated);
          props.updateLoyaltyCardAction({ status: ConstantStatus.Deactivated });
          toast.success('Loyalty card deactivated');
          break;
        case ModalContent.Status.Update.actionName:
          await onSaveAllClick();
          break;
        case ModalContent.VoucherIssuance.Pause.actionName:
          await ApiService.pauseVouchersIssuance(props.page.id, props.match.params.loyaltyCardId, true);
          props.updateLoyaltyCardAction({ pauseVoucherIssuance: true });
          toast.success('Voucher issuance has been paused');
          break;
        case ModalContent.VoucherIssuance.Continue.actionName:
          await ApiService.pauseVouchersIssuance(props.page.id, props.match.params.loyaltyCardId, false);
          props.updateLoyaltyCardAction({ pauseVoucherIssuance: false });
          toast.success('Voucher issuance has been resumed');
          break;
        default:
          return;
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const fetchOutlets = async () => {
    try {
      props.dispatchStartSavingLC();

      const outlets = await ApiService.getOutlets(props.page.id);
      const participateOutlets = await ApiService.getParticipateOutlets(props.page.id, props.match?.params?.loyaltyCardId);

      props.updateLoyaltyCardAction({
        outlets: outlets.map((o) => ({
          id:         o.id,
          name:       o.name,
          address:    o.address,
          postalCode: o.postalCode,
          unitNo:     o.unitNo,
        })),
        participateOutlets: participateOutlets.rows.map((o) => ({
          id:         o.id,
          name:       o.brand,
          address:    o.address,
          postalCode: o.postalCode,
          unitNo:     o.unitNo,
        })),
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  useEffect(() => {
    fetchOutlets();
  }, []);

  return (
    <div className="container-fluid px-0">
      {
        props.savingLC && (
          <Loading />
        )
      }
      {getSelectedTabContent()}
      {
        props.showSaveBtn && (
          props.withAdvancedStatusBtn ? (
            <div className="row px-0">
              <div className="col-12 col-md-6 col-lg-8">
                <div className="row mb-5">
                  <div className="col-4 px-1 d-flex justify-content-center">
                    <div className={classes.statusBtnContainer}>
                      <MButton
                        text="Save Draft"
                        style={{ border: loyaltyStatus === ConstantStatus.Draft ? `2px solid ${Colours.Black}` : undefined }}
                        textStyle={{ fontWeight: 600 }}
                        className={classes.statusBtn}
                        disableElevation
                        disabled={props.savingLC || props.disableSaveBtn || loyaltyStatus !== ConstantStatus.Draft}
                        textColour={loyaltyStatus === ConstantStatus.Draft ? Colours.Black : Colours.Gray7}
                        colour={loyaltyStatus === ConstantStatus.Draft ? Colours.White : Colours.LightGray3}
                        onClick={onSaveAllClick}
                      />
                      <MText
                        className="text-center mt-2"
                        text="Editing mode, loyalty card not ready for public release."
                        size={11}
                        colour={loyaltyStatus === ConstantStatus.Draft ? Colours.Black : Colours.LightGray4}
                      />
                    </div>
                  </div>
                  <div className="col-4 px-1 d-flex justify-content-center">
                    <div className={classes.statusBtnContainer}>
                      <MButton
                        text="Live"
                        style={{ border: loyaltyStatus === ConstantStatus.Live ? `2px solid ${Colours.Black}` : undefined }}
                        textStyle={{ fontWeight: 600 }}
                        className={classes.statusBtn}
                        disableElevation
                        disabled={props.savingLC || props.disableSaveBtn || loyaltyStatus !== ConstantStatus.Draft}
                        textColour={loyaltyStatus !== ConstantStatus.Deactivated ? Colours.White : Colours.Gray7}
                        colour={loyaltyStatus !== ConstantStatus.Deactivated ? Colours.Green1 : Colours.LightGray3}
                        onClick={() => {
                          setShowConfirmationModal(true);
                          setModalContent(ModalContent.Status.Live);
                        }}
                      />
                      <MText
                        className="text-center mt-2"
                        text="Users can access loyalty card and start collecting stamps"
                        size={11}
                        colour={loyaltyStatus !== ConstantStatus.Deactivated ? Colours.Black : Colours.LightGray4}
                      />
                      {
                        loyaltyStatus === ConstantStatus.Live && (
                          <div className="d-flex flex-column align-items-center mt-4">
                            <MButton
                              text="Update"
                              textStyle={{ fontWeight: 600 }}
                              className={classes.statusBtn}
                              disableElevation
                              textColour={loyaltyStatus !== ConstantStatus.Deactivated ? Colours.White : Colours.Gray7}
                              colour={Colours.Secondary}
                              onClick={() => {
                                setShowConfirmationModal(true);
                                setModalContent(ModalContent.Status.Update);
                              }}
                            />
                            <MText
                              className="text-center mt-2"
                              text="Activate changes made to live Loyalty Cards"
                              size={11}
                              colour={loyaltyStatus !== ConstantStatus.Deactivated ? Colours.Black : Colours.LightGray4}
                            />
                          </div>
                        )
                      }
                    </div>
                  </div>
                  <div className="col-4 px-1 d-flex justify-content-center">
                    <div className={classes.statusBtnContainer}>
                      <MButton
                        text="Deactivate"
                        style={{ border: loyaltyStatus === ConstantStatus.Deactivated ? `2px solid ${Colours.Black}` : undefined }}
                        textStyle={{ fontWeight: 600 }}
                        className={classes.statusBtn}
                        disableElevation
                        disabled={props.savingLC || props.disableSaveBtn || loyaltyStatus !== ConstantStatus.Live}
                        textColour={Colours.White}
                        colour={loyaltyStatus !== ConstantStatus.Draft ? Colours.DarkGray : Colours.Gray4}
                        onClick={() => {
                          setShowConfirmationModal(true);
                          setModalContent(ModalContent.Status.Deactivate);
                        }}
                      />
                      <MText
                        className="text-center mt-2"
                        text="Termination of users' access to the loyalty card. Please note that reactivation is not possible."
                        size={11}
                        colour={Colours.DarkGray1}
                      />
                    </div>
                  </div>
                </div>
                {
                  loyaltyStatus === ConstantStatus.Live && (
                    <div className="mb-4">
                      <div style={{ marginLeft: 12 }}>
                        <MText text="Pause voucher issuance" />
                        <MText size={11} colour={Colours.DarkGray1} text="Stop voucher issuance temporarily. Users can continue to collect stamps." />
                      </div>
                      <CheckBox
                        checked={props.loyaltyProgram.pauseVoucherIssuance}
                        // onChange={(checked) => props.updateLoyaltyCardAction({ pauseVoucherIssuance: checked })}
                        onChange={(checked) => {
                          setShowConfirmationModal(true);
                          if (checked) {
                            setModalContent(ModalContent.VoucherIssuance.Pause);
                          } else {
                            setModalContent(ModalContent.VoucherIssuance.Continue);
                          }
                        }}
                        text="Pause voucher issuance"
                      />
                    </div>
                  )
                }
              </div>
            </div>
          ) : (
            <div className="row mb-5">
              <div className="col-md-3 col-lg-4 col-xl-5" />
              <div className="col-12 col-md-6 col-lg-4 col-xl-2 px-0 d-flex justify-content-center">
                {
                  loyaltyStatus === ConstantStatus.Draft && (
                    <div className={classes.statusBtnContainer}>
                      <MButton
                        text="Save Draft"
                        style={{ border: loyaltyStatus === ConstantStatus.Draft ? `2px solid ${Colours.Black}` : undefined }}
                        textStyle={{ fontWeight: 600 }}
                        className={classes.statusBtn}
                        disableElevation
                        disabled={props.savingLC || props.disableSaveBtn || loyaltyStatus !== ConstantStatus.Draft}
                        textColour={loyaltyStatus === ConstantStatus.Draft ? Colours.Black : Colours.Gray7}
                        colour={loyaltyStatus === ConstantStatus.Draft ? Colours.White : Colours.LightGray3}
                        onClick={onSaveAllClick}
                      />
                      <MText
                        className="text-center mt-2"
                        text="Editing mode, loyalty card not ready for public release."
                        size={11}
                        colour={loyaltyStatus === ConstantStatus.Draft ? Colours.Black : Colours.LightGray4}
                      />
                    </div>
                  )
                }
              </div>
              <div className="col-md-3 col-lg-4 col-xl-5" />
            </div>
          )
        )
      }
      <AlertModal
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        action={{
          name: modalContent.actionName,
          func: onConfirm,
        }}
        alert={{
          title:       modalContent.title,
          description: modalContent.description,
        }}
      />
    </div>
  );
};

Main.propTypes = {
  tab: PropTypes.oneOf([
    LoyaltyHeaderMenu.Design,
    LoyaltyHeaderMenu.Receipt,
    LoyaltyHeaderMenu.StampAndMechanics,
    LoyaltyHeaderMenu.Status,
    LoyaltyHeaderMenu.ValidityAndTerms,
    LoyaltyHeaderMenu.Voucher,
    LoyaltyHeaderMenu.Outlets,
  ]).isRequired,
  loyaltyProgram: PropTypes.shape({
    name:             PropTypes.string,
    expiryDate:       PropTypes.string,
    validityInMonths: PropTypes.number,
    additionalTnC:    PropTypes.string,
    stamp:            PropTypes.shape({
      numOfStamps:    PropTypes.number,
      amountPerStamp: PropTypes.number,
    }),
    design: PropTypes.shape({
      designType:                   PropTypes.oneOf([DesignTypeOptions.Template, DesignTypeOptions.Manual]),
      uploadedDesign:               PropTypes.string,
      stampsHolderBackgroundColour: PropTypes.string,
      templatePreview:              PropTypes.string,
      selectedLoyaltyCardTemplate:  PropTypes.string,
      selectedStampsHolderTemplate: PropTypes.string,
    }),
    status:               PropTypes.oneOf([ConstantStatus.Draft, ConstantStatus.Live, ConstantStatus.Deactivated]),
    pauseVoucherIssuance: PropTypes.bool,
    participateOutlets:   PropTypes.arrayOf(PropTypes.shape({
      id:         PropTypes.number,
      name:       PropTypes.string,
      address:    PropTypes.string,
      postalCode: PropTypes.string,
      unitNo:     PropTypes.string,
    })),
    vouchers: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  page: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  savingLC:       PropTypes.bool.isRequired,
  disableSaveBtn: PropTypes.bool.isRequired,
  showSaveBtn:    PropTypes.bool.isRequired,
  match:          PropTypes.shape({
    params: PropTypes.shape({
      loyaltyCardId: PropTypes.string,
    }),
  }).isRequired,
  withAdvancedStatusBtn: PropTypes.bool.isRequired,

  dispatchStartSavingLC:   PropTypes.func.isRequired,
  dispatchFinishSavingLC:  PropTypes.func.isRequired,
  updateLoyaltyCardAction: PropTypes.func.isRequired,
};

export default withLoyaltyConnect(Main);
